import { type DeepPartial, isDefined, merge, uuid } from "@banjoanton/utils";
import { Move as ChessJsMove } from "chess.js";
import z from "zod/v4";
import { ChessUtil } from "../utils/chess-util";
import {
    AnalysisScoreTypeSchema,
    ChessColorsSchema,
    ChessPiecesSchema,
} from "./base-model";
import { EngineModel } from "./engine-model";
import type { CommentsMap } from "./move-comment";
import { MoveRating } from "./move-rating-model";
import { PositionUtil } from "../utils/position-util";
import { MoveAnnotationSchema } from "../features/annotations/models/move-annotation";
import { TimeControlUtil } from "../utils/time-control-util";

export const MoveScoreSchema = z.object({
    type: AnalysisScoreTypeSchema,
    score: z.number(),
});

export type MoveScore = z.infer<typeof MoveScoreSchema>;

export const MoveAnalysisSchema = z.object({
    annotations: z.array(MoveAnnotationSchema),
    rating: z.enum(MoveRating),
    centipawnLoss: z.number(),
    engineModel: z.enum(EngineModel),
    depth: z.number(),
});
export type MoveAnalysis = z.infer<typeof MoveAnalysisSchema>;

const CaptureLogSchema = z.object({
    pieces: ChessPiecesSchema.array(),
    score: z.number(),
});

export type CaptureLog = z.infer<typeof CaptureLogSchema>;

export const CaptureSummarySchema = z.object({
    w: CaptureLogSchema,
    b: CaptureLogSchema,
});

export type CaptureSummary = z.infer<typeof CaptureSummarySchema>;

export const MoveSchema = z.object({
    id: z.string().default(() => uuid()),
    fenBefore: z.string(),
    fenAfter: z.string(),
    to: z.string(),
    from: z.string(),
    san: z.string(),
    piece: ChessPiecesSchema,
    color: ChessColorsSchema,
    clockTimeMs: z.number().optional(),
    isCapture: z.boolean(),
    isPromotion: z.boolean(),
    captured: z.string().optional(),
    isKingsideCastle: z.boolean(),
    isQueensideCastle: z.boolean(),
    promotion: z.string().optional(),
    index: z.number(),
    analysis: MoveAnalysisSchema.optional(),
    captureSummary: CaptureSummarySchema,
});

export type Move = z.infer<typeof MoveSchema>;

const from = (
    chessJsMove: ChessJsMove,
    index: number,
    commentsMap: CommentsMap,
    captureSummary: CaptureSummary
): Move => {
    const comments = commentsMap[chessJsMove.after.toLowerCase()] || [];
    const clockTime = comments
        .map((c) => ChessUtil.parseClockTime(c.comment))
        .find(isDefined);

    const clockTimeMs = TimeControlUtil.parseChessComClockTimeMs(clockTime);

    if (chessJsMove.captured) {
        // add captured pieaces first to get correct score
        captureSummary[chessJsMove.color].pieces.push(chessJsMove.captured);

        const { whiteScore, blackScore } =
            PositionUtil.getScore(captureSummary);

        captureSummary.w.score = whiteScore;
        captureSummary.b.score = blackScore;
    }

    return {
        id: uuid(),
        fenBefore: chessJsMove.before,
        fenAfter: chessJsMove.after,
        to: chessJsMove.to,
        san: chessJsMove.san,
        clockTimeMs,
        from: chessJsMove.from,
        piece: chessJsMove.piece,
        color: chessJsMove.color,
        isCapture: chessJsMove.isCapture(),
        isPromotion: chessJsMove.isPromotion(),
        captured: chessJsMove.captured,
        isKingsideCastle: chessJsMove.isKingsideCastle(),
        isQueensideCastle: chessJsMove.isQueensideCastle(),
        promotion: chessJsMove.promotion,
        captureSummary: structuredClone(captureSummary),
        index,
    };
};

const update = (current: Move, updated: DeepPartial<Move>) =>
    merge(current, updated);

export const Move = {
    from,
    update,
};
